/**
 * ai-usage-tab.tsx — Theo dõi › "Thống kê sử dụng": lượt AI, lần gọi / token thật / chi phí nhà cung cấp, theo việc + nguồn,
 * lịch sử lượt AI. Phần trừ của "AI của eCDP" hiện theo LƯỢT AI (không đ); số token là token THẬT đã dùng (không quy đổi ngược).
 */
import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Activity, ArrowDownToLine, Coins, Hash, Wallet } from 'lucide-react'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatNumber } from '@/lib/utils'
import { currentLocale, i18n } from '@/lib/i18n'
import { useAiUsage, type AiUsage } from '@/hooks/use-ai'
import { useAiCredits, useAiLedger, useAiModels } from '@/hooks/use-ai-credits'
import {
  aiErrorMessage, amountText, featureLabel, formatSignedTurns, formatTokens, formatTurns, ledgerKindLabel, ledgerTurns, SOURCE_LABEL,
  tokenRateOf,
  type AiSource, type LedgerItem, type TokenRate,
} from '@/lib/ai-credits'
import { ToneBadge, WalletStrip } from '../credits/credit-widgets'

export function AiUsageTab({ onBuy }: { onBuy: () => void }) {
  const { t } = useTranslation('aiOps')
  const [days, setDays] = useState(1)
  const { data, isLoading, isError } = useAiUsage(days)
  const { data: credits } = useAiCredits()
  const { data: modelsData } = useAiModels()
  const rate = tokenRateOf(credits)

  const byTypeRows = useMemo(
    () => Object.entries(data?.byType ?? {}).map(([type, count]) => ({ type, count })),
    [data],
  )

  const cols: Column<{ type: string; count: number }>[] = [
    { key: 'type', header: t('usage.cols.task'), cell: (r) => <span className="font-medium">{featureLabel(r.type)}</span> },
    { key: 'count', header: t('usage.cols.calls'), align: 'right', cell: (r) => formatNumber(r.count) },
  ]

  const periodLabel = days === 1 ? t('common:time.today') : t('usage.daysAgo', { count: days })

  return (
    <div className="space-y-6">
      {credits && (
        <WalletStrip
          credits={credits}
          planName={modelsData?.plan?.name}
          onBuy={onBuy}
          onHistory={() => document.getElementById('ai-credit-ledger')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('usage.intro')}</p>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">{t('common:time.today')}</SelectItem>
            <SelectItem value="7">{t('usage.days7')}</SelectItem>
            <SelectItem value="30">{t('usage.days30')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Loading label={t('usage.loading')} />
      ) : isError || !data ? (
        <ErrorState message={t('usage.loadError')} />
      ) : (
        <>
          <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${credits ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
            <StatCard label={t('usage.callsToday')} value={data.callsToday} icon={Activity} hint={t('usage.remaining', { remaining: formatNumber(data.remaining), max: formatNumber(data.maxDaily) })} />
            <StatCard label={t('usage.tokensIn')} value={data.tokensIn} icon={ArrowDownToLine} />
            <StatCard label={t('usage.tokensOut')} value={data.tokensOut} icon={Hash} tone="success" />
            <StatCard label={t('usage.costUsd')} value={`$${data.costUsd.toFixed(4)}`} icon={Coins} tone="warning" hint={t('usage.cacheRead', { n: formatNumber(data.cacheReadTokens) })} />
            {credits && (
              <StatCard
                label={t('usage.usedThisMonth')}
                value={amountText(credits.usedThisMonth, rate)}
                icon={Wallet}
                hint={credits.monthlyGrant ? t('usage.monthlyGrant', { amount: amountText(credits.monthlyGrant, rate) }) : t('usage.creditsDeducted')}
              />
            )}
          </div>

          {data.byFeature ? (
            <FeatureSourceCard rows={data.byFeature} bySource={data.bySource} periodLabel={periodLabel} rate={rate} />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t('usage.byTaskTitle')}</CardTitle>
                <CardDescription>{t('usage.byTaskDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <DataTable columns={cols} rows={byTypeRows} rowKey={(r) => r.type} emptyTitle={t('usage.byTaskEmpty')} />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {credits && <LedgerCard />}
    </div>
  )
}

type FeatureRow = NonNullable<AiUsage['byFeature']>[number]

function SourceTag({ source }: { source: AiSource }) {
  return <ToneBadge tone={source === 'platform' ? 'default' : 'success'}>{SOURCE_LABEL[source] ?? source}</ToneBadge>
}

/** "Theo việc và nguồn" — dòng Key của tôi không trừ ví; dòng eCDP: số tiền đã trừ (đ, giá bán ra). */
function FeatureSourceCard({ rows, bySource, periodLabel, rate }: {
  rows: FeatureRow[]; bySource?: AiUsage['bySource']; periodLabel: string; rate: TokenRate | null
}) {
  const { t } = useTranslation('aiOps')
  const cols: Column<FeatureRow>[] = [
    { key: 'feature', header: t('usage.cols.task'), cell: (r) => <span className="font-medium">{featureLabel(r.feature)}</span> },
    { key: 'model', header: t('usage.cols.model'), cell: (r) => r.model ?? '—' },
    { key: 'source', header: t('usage.cols.source'), cell: (r) => <SourceTag source={r.source} /> },
    { key: 'calls', header: t('usage.cols.calls'), align: 'right', cell: (r) => formatNumber(r.calls) },
    { key: 'credits', header: t('usage.cols.creditsDeducted'), align: 'right', cell: (r) => (r.source === 'platform' && r.credits ? amountText(r.credits, rate) : '—') },
  ]
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('usage.byTaskSourceTitle')}</CardTitle>
        <CardDescription>
          {t('usage.byTaskSourceDesc', { period: periodLabel })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {bySource && bySource.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {bySource.map((s) => (
              <div key={s.source} className="rounded-lg border p-3 text-sm">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <SourceTag source={s.source} />
                  <span className="text-xs text-muted-foreground">{t('usage.callsCount', { n: formatNumber(s.calls) })}</span>
                </div>
                {s.source === 'platform' ? (
                  <p>
                    <Trans t={t} i18nKey="usage.deducted" values={{ amount: amountText(s.credits, rate) }} components={{ b: <b className="tabular-nums" /> }} />
                    <span className="text-muted-foreground"> · {t('usage.realTokens', { amount: formatTokens(s.tokensIn + s.tokensOut) })}</span>
                  </p>
                ) : (
                  <p><Trans t={t} i18nKey="usage.estimatedCost" values={{ amount: `$${s.costUsd.toFixed(4)}` }} components={{ b: <b className="tabular-nums" /> }} /></p>
                )}
              </div>
            ))}
          </div>
        )}
        <DataTable
          columns={cols} rows={rows} rowKey={(r) => `${r.feature}:${r.model ?? ''}:${r.source}`}
          emptyTitle={t('usage.periodEmpty')}
        />
      </CardContent>
    </Card>
  )
}

/** Nội dung dòng sổ cái: dùng AI gộp theo (ngày, việc) → "Việc · N lần gọi · 34,5 nghìn token thật". */
function ledgerNote(r: LedgerItem): string {
  if (r.kind === 'consume') {
    const f = r.feature || r.bucket
    const used = r.usage ? r.usage.tokensIn + r.usage.tokensOut : 0
    const parts = [
      f ? featureLabel(f) : null, r.calls ? i18n.t('aiOps:usage.callsNote', { n: formatNumber(r.calls) }) : null,
      used > 0 ? i18n.t('aiOps:usage.realTokens', { amount: formatTokens(used) }) : null, r.note,
    ].filter(Boolean)
    return parts.join(' · ') || '—'
  }
  return r.note || '—'
}

/** Lịch sử lượt AI — mua, tặng, dùng, điều chỉnh. Số theo lượt AI. */
function LedgerCard() {
  const { t } = useTranslation('aiOps')
  const q = useAiLedger(20)
  const { data: credits } = useAiCredits()
  const rate = tokenRateOf(credits)
  const items = (q.data?.pages ?? []).flatMap((p) => p?.items ?? [])
  const missing = q.data?.pages[0] === null
  if (missing) return null
  return (
    <Card id="ai-credit-ledger" className="scroll-mt-4">
      <CardHeader>
        <CardTitle>{t('usage.ledger.title')}</CardTitle>
        <CardDescription>
          {t('usage.ledger.desc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isLoading ? (
          <Loading label={t('usage.ledger.loading')} />
        ) : q.isError ? (
          <ErrorState message={aiErrorMessage(q.error, t('usage.ledger.loadError'))} />
        ) : (
          <DataTable
            columns={[
              { key: 'at', header: t('usage.ledger.date'), cell: (r) => <span className="text-muted-foreground">{new Date(r.createdAt).toLocaleString(currentLocale())}</span> },
              { key: 'kind', header: t('usage.ledger.kind'), cell: (r) => ledgerKindLabel(r.kind) },
              { key: 'note', header: t('usage.ledger.note'), cell: (r) => ledgerNote(r) },
              {
                key: 'amount', header: t('usage.ledger.amount'), align: 'right',
                cell: (r) => <span className={r.amount >= 0 ? 'text-success' : ''}>{formatSignedTurns(ledgerTurns(r, rate).amount)}</span>,
              },
              { key: 'after', header: t('usage.ledger.after'), align: 'right', cell: (r) => formatTurns(ledgerTurns(r, rate).after, '') },
            ]}
            rows={items}
            rowKey={(r) => r.id}
            emptyTitle={t('usage.ledger.empty')}
          />
        )}
        {q.hasNextPage && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
              {q.isFetchingNextPage ? t('common:status.loading') : t('common:actions.showMore')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
